/**
 * Machine page › Maximo record.
 *
 * The demo's `mxView(id)` (line 2186): the IBM Maximo service asset record —
 * identity and contract, life to date with cost per year and major events, the
 * PM schedule read off the meter, sub-assets, work-order history cross-
 * referenced to the customer CMMS, failure reports and linked documents.
 *
 * Fetched one machine at a time (`loadMachineRecord`, ~15 KB) rather than from
 * the 317 KB fleet-wide `service` domain, which is what that split exists for.
 *
 * ## `downtime_h` on a Maximo history row
 *
 * The source writes `''` — not `0` — where downtime was never recorded. The
 * column prints an em dash for that and a figure for a real zero, so a reader
 * is never told a breakdown cost no production when nobody measured it. The
 * dash carries "not recorded" as a tooltip and as screen-reader text, so the
 * distinction is stated rather than left to the reader to infer from a glyph.
 *
 * UI layer.
 *
 * @see docs/logic/fleet/machine-page.md
 */

import { useEffect, useState } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import type { MaximoRecord } from '../../schema/service';
import { fmtk, groupNum } from '../../shared/oem-engine';
import { loadMachineRecord } from '../../shared/useOemData';
import { TabLoader } from './TabLoader';

export interface MachineRecordTabProps {
  machineId: string;
}

/**
 * Load one machine's Maximo bundle. A local hook rather than `useOemData`,
 * which is keyed by domain and does not model per-document fetches.
 */
export function useMachineRecord(machineId: string) {
  const [state, setState] = useState<{
    record: MaximoRecord | null;
    loading: boolean;
    error: Error | null;
  }>({ record: null, loading: true, error: null });

  useEffect(() => {
    let live = true;
    // An empty id means the scene has no machine to show; never fetch
    // `/data/oem/maximo/.json`, which would be a 404 in the network panel.
    if (!machineId) {
      setState({ record: null, loading: false, error: null });
      return;
    }
    setState({ record: null, loading: true, error: null });
    loadMachineRecord(machineId).then(
      (record) => live && setState({ record, loading: false, error: null }),
      (cause: unknown) =>
        live &&
        setState({
          record: null,
          loading: false,
          error: cause instanceof Error ? cause : new Error(String(cause)),
        }),
    );
    return () => {
      live = false;
    };
  }, [machineId]);

  return state;
}

export function MachineRecordTab({ machineId }: MachineRecordTabProps) {
  const { record, loading, error } = useMachineRecord(machineId);

  return (
    <TabLoader loading={loading || !record} error={error} what="The Maximo record" rows={8}>
      {record ? <RecordBody record={record} /> : null}
    </TabLoader>
  );
}

function RecordBody({ record }: { record: MaximoRecord }) {
  const mx = record.mx;
  const years = mx.lifetime.years;
  const maxCost = Math.max(...years.map((y) => y.cost_aud), 1);

  return (
    <div className="@container space-y-6">
      <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
        <Panel
          title="Service asset record"
          subtitle={`${mx.siteid} · ${mx.orgid}`}
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <Field label="Asset">
              <span className="mono">
                {mx.assetnum} · {mx.serialnum}
              </span>
            </Field>
            <Field label="Status">
              {mx.status} · priority {mx.priority}
            </Field>
            <Field label="Location">
              <span className="mono">{mx.location}</span>
            </Field>
            <Field label="Custodian">
              {mx.custodian} · {mx.ownergroup}
            </Field>
            <Field label="Installed">
              {mx.installdate} · list A${groupNum(mx.purchaseprice)}
            </Field>
            <Field label="Warranty">
              {mx.warranty.status} · to {mx.warranty.end} / {groupNum(mx.warranty.hours_limit)} h
            </Field>
            <Field label="Contract">
              {mx.servicecontract.contractnum} · {mx.servicecontract.type} · to{' '}
              {mx.servicecontract.end}
            </Field>
            <Field label="Class">
              <span className="mono text-xs">{mx.classstructure.classificationid}</span>
            </Field>
          </dl>

          <h4 className="mt-4 text-xs uppercase tracking-wider text-muted">Meters</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {mx.assetmeter.map((meter) => (
              <li key={meter.metername} className="flex justify-between gap-3">
                <span>
                  {meter.description} <span className="text-muted">({meter.metername})</span>
                </span>
                <span className="mono">{groupNum(meter.lastreading)}</span>
              </li>
            ))}
          </ul>
          {mx.assetmeter[0] ? (
            <p className="mt-2 text-[11px] text-muted">Source: {mx.assetmeter[0].source}</p>
          ) : null}
        </Panel>

        <Panel
          title="Life to date"
          subtitle={`${years[0]?.year ?? ''}–2026 · cost per year, A$`}
        >
          <ul className="space-y-2">
            {years.map((y) => (
              <li key={y.year} className="space-y-1">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <span>
                    <b className="mono text-zinc-900 dark:text-zinc-100">{y.year}</b>{' '}
                    <span className="text-muted">
                      {y.pm} PM · {y.cm} CM · {y.breakdown} breakdowns · {groupNum(y.hours)} h ·{' '}
                      {y.availability_pct}% avail.
                    </span>
                  </span>
                  <span className="mono shrink-0">{fmtk(y.cost_aud)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.round((y.cost_aud / maxCost) * 100)}%`,
                      background: y.breakdown >= 3 ? 'var(--band-vpoor)' : 'var(--state-info)',
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>

          <h4 className="mt-4 text-xs uppercase tracking-wider text-muted">Major events</h4>
          <ol className="mt-1 space-y-1 text-sm">
            {mx.lifetime.major_events.map((e, i) => (
              <li key={`${e.date}#${i}`} className="flex gap-3">
                <time className="mono shrink-0 text-muted">{e.date}</time>
                <span>
                  {e.event}
                  {e.meter != null ? (
                    <span className="text-muted"> · {groupNum(e.meter)} h</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      <Panel title="PM schedule" subtitle="From the meter, not the calendar">
        <Table>
          <THead>
            <TR>
              <TH>PM</TH>
              <TH>Job plan</TH>
              <TH className="text-right">Every</TH>
              <TH className="text-right">Next due</TH>
              <TH>Date</TH>
              <TH>Last</TH>
              <TH>Contract</TH>
            </TR>
          </THead>
          <TBody>
            {mx.pm.map((pm) => (
              <TR key={pm.pmnum}>
                <TD className="mono">{pm.pmnum.split('-').slice(-1)[0]} h</TD>
                <TD className="mono">{pm.jobplan}</TD>
                <TD className="mono text-right">{pm.frequency} h</TD>
                <TD className="mono text-right">{groupNum(pm.nextdue_meter)} h</TD>
                <TD className="mono">{pm.nextdue_date}</TD>
                <TD className="mono">{pm.lastcompleted}</TD>
                <TD>
                  {pm.contract_interval !== pm.frequency ? (
                    <span className="band-pill band-poor">
                      contract {pm.contract_interval} h — longer than OEM
                    </span>
                  ) : (
                    <span className="text-muted">= OEM</span>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>

        <h4 className="mt-4 text-xs uppercase tracking-wider text-muted">Sub-assets</h4>
        <ul className="mt-1 space-y-1 text-sm">
          {mx.children.map((child) => (
            <li key={child.assetnum} className="flex items-center gap-3">
              <span className={`band-dot band-${child.condition_band}`} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                {child.description}{' '}
                <span className="text-muted">
                  · {groupNum(child.hours_since_change)} h since change
                </span>
              </span>
              <span className="mono shrink-0">{child.condition_score}</span>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel
        title="Work-order history"
        subtitle={`Last ${mx.wohistory.length} · cross-referenced to ${
          mx.wohistory[0]?.source_system ?? 'the customer CMMS'
        }`}
      >
        <Table>
          <THead>
            <TR>
              <TH>Reported</TH>
              <TH>WONUM</TH>
              <TH>Platform ref</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH>Description</TH>
              <TH className="text-right">Down</TH>
            </TR>
          </THead>
          <TBody>
            {mx.wohistory.map((w, i) => (
              <TR key={`${w.wonum}#${i}`}>
                <TD className="mono whitespace-nowrap">{w.reportdate.slice(0, 10)}</TD>
                <TD className="mono">{w.wonum}</TD>
                <TD className="mono">{w.externalrefid}</TD>
                <TD className="whitespace-nowrap">{w.worktype}</TD>
                <TD className="whitespace-nowrap">{w.status}</TD>
                <TD className="max-w-[40ch]">{w.description}</TD>
                <TD className="mono text-right">
                  {w.downtime_h === '' ? (
                    <span title="Downtime not recorded in Maximo for this work order">
                      <span aria-hidden>—</span>
                      <span className="sr-only">not recorded</span>
                    </span>
                  ) : (
                    `${w.downtime_h} h`
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>

        <h4 className="mt-4 text-xs uppercase tracking-wider text-muted">
          Failure reports — problem · cause · remedy
        </h4>
        {mx.failurereports.length ? (
          <ul className="mt-1 space-y-1 text-[11.5px]">
            {mx.failurereports.slice(0, 6).map((r, i) => (
              <li key={`${r.wonum}#${i}`} className="border-t border-divider pt-1">
                <span className="mono text-muted">{r.date}</span> {r.component} —{' '}
                <b>{r.problem}</b> · {r.cause} · {r.remedy}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-muted">None in 18 months.</p>
        )}

        <h4 className="mt-4 text-xs uppercase tracking-wider text-muted">Linked documents</h4>
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {mx.doclinks.map((d, i) => (
            <li
              key={`${d.doctype}#${i}`}
              className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] text-muted dark:bg-zinc-800"
            >
              {d.doctype}: {d.document.split('/').pop()?.replace(/#.*$/, '').slice(0, 42)}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
        {subtitle ? <span className="ml-2 font-normal text-muted">{subtitle}</span> : null}
      </h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100">{children}</dd>
    </>
  );
}
